import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Panel, StatusBadge, EmptyState } from "@/components/admin/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { LEAD_STATUSES, HEARD_FROM, GENDERS } from "@/lib/constants";
import { Search, ShieldCheck } from "lucide-react";

const ALL = "__all__";

export default function VisitorLog() {
  const navigate = useNavigate();
  const [visitors, setVisitors] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ALL);
  const [source, setSource] = useState(ALL);
  const [gender, setGender] = useState(ALL);

  const load = async () => {
    const p = new URLSearchParams();
    if (search) p.set("search", search);
    if (status !== ALL) p.set("stage", status);
    if (source !== ALL) p.set("source", source);
    if (gender !== ALL) p.set("gender", gender);
    setVisitors((await api.get(`/visitors?${p.toString()}`)).data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, status, source, gender]);

  return (
    <div>
      <PageHeader title="Visitor Log" subtitle={`${visitors.length} visitor profiles`} />
      <Panel className="mb-5">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="visitor-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or mobile…" className="pl-9 h-10 rounded-xl" />
          </div>
          <FilterSelect value={status} onChange={setStatus} placeholder="Stage" options={LEAD_STATUSES} testid="filter-status" />
          <FilterSelect value={source} onChange={setSource} placeholder="Source" options={HEARD_FROM} testid="filter-source" />
          <FilterSelect value={gender} onChange={setGender} placeholder="Gender" options={GENDERS} testid="filter-gender" />
        </div>
      </Panel>

      <Panel>
        {visitors.length === 0 ? <EmptyState text="No visitors found" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-3 px-2">Visitor ID</th><th className="px-2">Name</th><th className="px-2">Mobile</th>
                  <th className="px-2">Area</th><th className="px-2">Source</th><th className="px-2">Visits</th><th className="px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {visitors.map((v) => (
                  <tr key={v.visitor_id} data-testid="visitor-row" onClick={() => navigate(`/admin/visitors/${v.visitor_id}`)}
                    className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                    <td className="py-3 px-2 font-mono-num text-xs text-[#B8860B] font-bold">{v.visitor_id}</td>
                    <td className="px-2 font-semibold text-[#081226]">
                      <span className="inline-flex items-center gap-1">{v.full_name}{v.ekyc_verified && <ShieldCheck size={14} className="text-emerald-500 shrink-0" data-testid="verified-tick" />}</span>
                    </td>
                    <td className="px-2 text-slate-500 font-mono-num">{v.mobile}</td>
                    <td className="px-2 text-slate-500">{v.area || "—"}</td>
                    <td className="px-2 text-slate-500">{v.source}</td>
                    <td className="px-2 text-center font-bold">{v.total_visits}</td>
                    <td className="px-2"><StatusBadge status={v.lead_stage || "New"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[150px] h-10 rounded-xl bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {placeholder}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
